import React, { useState, useEffect, useCallback } from "react";
import config from "../config";

export default function AdminUserManagement({ adminToken }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${config.API_URL}/admin/users`, {
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      setError("Could not load users");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) fetchUsers();
  }, [adminToken, fetchUsers]);

  // Delete user
  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Delete user "${username}"? This will remove all their data and cannot be undone.`)) return;
    setError("");
    try {
      const res = await fetch(`${config.API_URL}/admin/users/${userId}`, {
        method: "DELETE",
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) throw new Error("Failed to delete user");
      fetchUsers();
    } catch (err) {
      setError("Could not delete user");
    }
  };

  // Change admin email
  const handleChangeEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }
    
    setChangingEmail(true);
    setError("");
    try {
      const res = await fetch(`${config.API_URL}/admin/change-email`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "token": adminToken 
        },
        body: JSON.stringify({ new_email: newEmail })
      });
      if (!res.ok) throw new Error("Failed to change email");
      
      setNewEmail("");
      setShowEmailChange(false);
      fetchUsers(); // Refresh the user list to show updated email
      alert("Admin email changed successfully!");
    } catch (err) {
      setError("Could not change email");
    } finally {
      setChangingEmail(false);
    }
  };

  // Change admin password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    
    setChangingPassword(true);
    setError("");
    try {
      const res = await fetch(`${config.API_URL}/admin/change-password`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "token": adminToken 
        },
        body: JSON.stringify({ new_password: newPassword })
      });
      if (!res.ok) throw new Error("Failed to change password");
      
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordChange(false);
      alert("Admin password changed successfully!");
    } catch (err) {
      setError("Could not change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Export user data
  const handleExportUser = async (userId, username) => {
    try {
      const res = await fetch(`${config.API_URL}/admin/users/${userId}/export`, {
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) throw new Error("Failed to export user data");
      const data = await res.json();
      
      // Create JSON file
      const jsonBlob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const jsonUrl = window.URL.createObjectURL(jsonBlob);
      const jsonLink = document.createElement('a');
      jsonLink.href = jsonUrl;
      jsonLink.download = `user_${username}_data.json`;
      document.body.appendChild(jsonLink);
      jsonLink.click();
      window.URL.revokeObjectURL(jsonUrl);
      document.body.removeChild(jsonLink);
      
      // Create CSV file
      const csvData = [
        ['User ID', 'Username', 'Email', 'Verified', 'Export Date'],
        [data.user_id, data.username, data.email, data.is_verified ? 'Yes' : 'No', data.export_date]
      ];
      
      // Add league data
      if (data.leagues && data.leagues.length > 0) {
        csvData.push(['']); // Empty row
        csvData.push(['League Memberships']);
        csvData.push(['League ID', 'League Name', 'Role']);
        data.leagues.forEach(league => {
          csvData.push([league.league_id, league.league_name, league.role]);
        });
      }
      
      const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const csvBlob = new Blob([csvContent], { type: 'text/csv' });
      const csvUrl = window.URL.createObjectURL(csvBlob);
      const csvLink = document.createElement('a');
      csvLink.href = csvUrl;
      csvLink.download = `user_${username}_data.csv`;
      document.body.appendChild(csvLink);
      csvLink.click();
      window.URL.revokeObjectURL(csvUrl);
      document.body.removeChild(csvLink);
      
    } catch (err) {
      setError("Could not export user data");
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );



  return (
    <div style={{ maxWidth: 900, margin: "40px auto", padding: 32, background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px #0001" }}>
      <h2 style={{ color: "#2563eb" }}>User Management</h2>
      
             {/* Admin Account Management Section */}
       <div style={{ marginBottom: 24, padding: 16, background: "#f8fafc", borderRadius: 8, border: "1px solid #e0e7ff" }}>
         <h3 style={{ color: "#374151", marginTop: 0, marginBottom: 12 }}>Admin Account Management</h3>
         
         {/* Password Change */}
         <div style={{ marginBottom: 16 }}>
           <h4 style={{ color: "#4b5563", marginBottom: 8 }}>Change Admin Password</h4>
        {!showPasswordChange ? (
          <button 
            onClick={() => setShowPasswordChange(true)}
            className="ffb-btn"
            style={{ background: "#059669" }}
          >
            Change Admin Password
          </button>
        ) : (
          <div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>New Password:</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>Confirm Password:</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button 
                onClick={handleChangePassword}
                disabled={changingPassword || !newPassword || !confirmPassword}
                className="ffb-btn"
                style={{ 
                  background: changingPassword ? "#9ca3af" : "#059669",
                  cursor: changingPassword ? "not-allowed" : "pointer"
                }}
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </button>
              <button 
                onClick={() => {
                  setShowPasswordChange(false);
                  setNewPassword("");
                  setConfirmPassword("");
                  setError("");
                }}
                className="ffb-btn"
                style={{ background: "#64748b" }}
              >
                Cancel
              </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Email Change */}
         <div style={{ marginBottom: 16 }}>
           <h4 style={{ color: "#4b5563", marginBottom: 8 }}>Change Admin Email</h4>
           {!showEmailChange ? (
             <button 
               onClick={() => setShowEmailChange(true)}
               className="ffb-btn"
               style={{ background: "#2563eb" }}
             >
               Change Admin Email
             </button>
           ) : (
             <div>
               <div style={{ marginBottom: 12 }}>
                 <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>New Email:</label>
                 <input
                   type="email"
                   value={newEmail}
                   onChange={e => setNewEmail(e.target.value)}
                   placeholder="Enter new email address"
                   style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #d1d5db" }}
                 />
               </div>
               <div style={{ display: "flex", gap: 8 }}>
                 <button 
                   onClick={handleChangeEmail}
                   disabled={changingEmail || !newEmail}
                   className="ffb-btn"
                   style={{ 
                     background: changingEmail ? "#9ca3af" : "#2563eb",
                     cursor: changingEmail ? "not-allowed" : "pointer"
                   }}
                 >
                   {changingEmail ? "Changing..." : "Change Email"}
                 </button>
                 <button 
                   onClick={() => {
                     setShowEmailChange(false);
                     setNewEmail("");
                     setError("");
                   }}
                   className="ffb-btn"
                   style={{ background: "#64748b" }}
                 >
                   Cancel
                 </button>
               </div>
             </div>
           )}
         </div>
       </div>
      
      {loading ? (
        <div>Loading users...</div>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <input
              type="text"
              placeholder="Search users by username or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #b6c3e0" }}
            />
          </div>
          
          {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
          
          <div style={{ maxHeight: "600px", overflowY: "auto", border: "1px solid #e0e7ff", borderRadius: 6 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#f8fafc", position: "sticky", top: 0 }}>
                <tr>
                  <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "1px solid #e0e7ff" }}>ID</th>
                  <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "1px solid #e0e7ff" }}>Username</th>
                  <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "1px solid #e0e7ff" }}>Email</th>
                  <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "1px solid #e0e7ff" }}>Verified</th>
                  <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "1px solid #e0e7ff" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px 8px" }}>{user.id}</td>
                    <td style={{ padding: "12px 8px" }}>{user.username}</td>
                    <td style={{ padding: "12px 8px" }}>{user.email}</td>
                    <td style={{ padding: "12px 8px" }}>
                      {user.is_verified ? (
                        <span style={{ color: '#059669' }}>✓</span>
                      ) : (
                        <span style={{ color: '#dc2626' }}>✗</span>
                      )}
                    </td>
                    <td style={{ padding: "12px 8px" }}>
                      <button 
                        onClick={() => handleExportUser(user.id, user.username)}
                        className="ffb-btn" 
                        style={{ 
                          marginRight: 8, 
                          background: "#2563eb", 
                          padding: "4px 8px", 
                          fontSize: "12px" 
                        }}
                      >
                        Export
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user.id, user.username)}
                        className="ffb-btn" 
                        style={{ 
                          background: "#dc2626", 
                          padding: "4px 8px", 
                          fontSize: "12px" 
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div style={{ padding: "20px", textAlign: "center", color: '#64748b' }}>
                {searchTerm ? "No users found matching your search." : "No users found."}
              </div>
            )}
          </div>
          
          <div style={{ marginTop: 16, color: '#64748b', fontSize: '14px' }}>
            Total users: {users.length} | Showing: {filteredUsers.length}
          </div>
        </>
      )}
    </div>
  );
} 